import * as React from "react";
import { FileText, Loader2, Printer, Trash2, Upload } from "lucide-react";
import { useAction } from "convex/react";
import { toast } from "sonner";

import {
  getDocumentUrl,
  DOC_TYPE_LABELS,
  type GeneratableDocType,
} from "@/lib/document-generation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/convexApi";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { hasPermission } from "@/lib/roles";
import { m } from "@/paraglide/messages.js";
import { useAppLocale } from "@/lib/i18n";

// ── Types ──────────────────────────────────────────────────────────────

export type DocumentLink = {
  key: string;
  label: string;
  url: string;
  documentType: string;
  source: "stored" | "live" | "external";
  /** orderDocuments _id — present only for stored rows that can be deleted. */
  documentId?: string;
};

type PrintDocumentResult =
  | { success: true }
  | {
      success: false;
      reason: "load_error" | "load_timeout" | "missing_window" | "print_error" | "print_timeout";
    };

type ResolvedDocumentUrl = {
  iframeUrl: string | null;
  openInNewTabUrl: string | null;
  objectUrl: string | null;
};

const DOCUMENT_PREVIEW_PROXY_PATH = "/api/document-preview-proxy";

const TRUSTED_IFRAME_HOST_SUFFIXES = [
  "anthemcoffeeimports.com",
  "railway.app",
  "up.railway.app",
];

const SAFE_DATA_URL_MEDIA_TYPES = new Set([
  "application/pdf",
  "image/gif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
]);

const IFRAME_SANDBOX = "allow-modals allow-same-origin allow-scripts";

function isTrustedIframeHost(hostname: string) {
  return TRUSTED_IFRAME_HOST_SUFFIXES.some(
    (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`),
  );
}

function isLocalDevelopmentHost(hostname: string) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
}

function decodeBase64ToBytes(value: string) {
  const normalizedValue = value.replace(/\s+/g, "");
  const binary = window.atob(normalizedValue);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function buildDocumentPreviewProxyUrl(url: string) {
  const trimmedUrl = String(url ?? "").trim();
  if (!trimmedUrl) return null;
  return `${DOCUMENT_PREVIEW_PROXY_PATH}?${new URLSearchParams({ url: trimmedUrl }).toString()}`;
}

function isBlobObjectUrl(url: string | null | undefined): url is string {
  return typeof url === "string" && url.startsWith("blob:");
}

function resolveSafeDataDocumentUrl(url: string): ResolvedDocumentUrl {
  const dataUrlMatch = /^data:([^;,]+)?((?:;[^,]*)*?),(.*)$/is.exec(url);
  const mediaType = String(dataUrlMatch?.[1] ?? "text/plain").trim().toLowerCase();
  const modifiers = String(dataUrlMatch?.[2] ?? "").toLowerCase();
  const rawData = String(dataUrlMatch?.[3] ?? "");

  if (!dataUrlMatch || !SAFE_DATA_URL_MEDIA_TYPES.has(mediaType)) {
    return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
  }

  if (typeof URL.createObjectURL === "function") {
    try {
      const blobPart = modifiers.includes(";base64")
        ? decodeBase64ToBytes(rawData)
        : decodeURIComponent(rawData);
      const objectUrl = URL.createObjectURL(new Blob([blobPart], { type: mediaType }));

      return {
        iframeUrl: objectUrl,
        openInNewTabUrl: objectUrl,
        objectUrl,
      };
    } catch {
      return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
    }
  }

  return {
    iframeUrl: url,
    openInNewTabUrl: url,
    objectUrl: null,
  };
}

function resolveDocumentUrl(url: string): ResolvedDocumentUrl {
  const trimmedUrl = String(url ?? "").trim();
  if (!trimmedUrl) {
    return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
  }

  if (trimmedUrl.startsWith("data:")) {
    return resolveSafeDataDocumentUrl(trimmedUrl);
  }

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.origin);
    const isSameOrigin = parsedUrl.origin === window.location.origin;
    const isLocalDevelopmentOrigin = isLocalDevelopmentHost(window.location.hostname);
    const isHttpUrl = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    if (!isHttpUrl) {
      return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
    }

    const isLocalDevelopmentUrl = isLocalDevelopmentHost(parsedUrl.hostname);

    if (!isSameOrigin && !isLocalDevelopmentUrl && parsedUrl.protocol !== "https:") {
      return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
    }

    const safeHref = parsedUrl.href;
    const canEmbedDirectly =
      isSameOrigin ||
      isLocalDevelopmentUrl ||
      (isTrustedIframeHost(parsedUrl.hostname) && !isLocalDevelopmentOrigin);
    const previewProxyUrl = canEmbedDirectly ? null : buildDocumentPreviewProxyUrl(safeHref);

    return {
      iframeUrl: canEmbedDirectly ? safeHref : previewProxyUrl,
      openInNewTabUrl: safeHref,
      objectUrl: previewProxyUrl,
    };
  } catch {
    return { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
  }
}

function getDocumentSourceLabel(source: DocumentLink["source"]) {
  if (source === "stored") return m.orders_document_source_pdf();
  if (source === "external") return m.orders_document_source_external();
  return m.orders_document_source_live();
}

function isDirectFileDocument(doc: DocumentLink) {
  return normalizeDocumentType(doc.documentType) === "shipping_label";
}

function getDataUrlMediaType(url: string) {
  const dataUrlMatch = /^data:([^;,]+)?/i.exec(url);
  return String(dataUrlMatch?.[1] ?? "").trim().toLowerCase();
}

function getDocumentFileKind(url: string) {
  const trimmedUrl = String(url ?? "").trim();
  if (!trimmedUrl) return "other" as const;

  if (trimmedUrl.startsWith("data:")) {
    const mediaType = getDataUrlMediaType(trimmedUrl);
    if (mediaType.startsWith("image/")) return "image" as const;
    if (mediaType === "application/pdf") return "pdf" as const;
    if (mediaType === "text/plain") return "text" as const;
  }

  try {
    const pathname = new URL(trimmedUrl, window.location.origin).pathname.toLowerCase();
    if (/\.(gif|heic|heif|jpe?g|png|svg|webp)$/.test(pathname)) return "image" as const;
    if (/\.(txt|zpl)$/.test(pathname)) return "text" as const;
    if (/\.pdf$/.test(pathname)) return "pdf" as const;
  } catch {
  }

  return "other" as const;
}

function getIframeSandboxForDocumentUrl(
  url: string | null | undefined,
  originalUrl?: string | null | undefined,
) {
  const isPdfDocument =
    getDocumentFileKind(url ?? "") === "pdf" ||
    getDocumentFileKind(originalUrl ?? "") === "pdf";

  // Chrome's built-in PDF viewer fails inside sandboxed iframes with
  // `chrome-error://chromewebdata` / `(blocked:other)`. Keep the sandbox for
  // live HTML document routes, but omit it for PDFs so hosted BOLs render.
  return isPdfDocument ? undefined : IFRAME_SANDBOX;
}

function printDocumentByUrl(
  url: string,
  iframeSandbox?: string,
): Promise<PrintDocumentResult> {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    let isSettled = false;
    let frameWindow: Window | null = null;
    let loadTimeoutId: number | null = null;
    let printTimeoutId: number | null = null;
    let didStartPrint = false;

    const settle = (result: PrintDocumentResult) => {
      if (isSettled) return;
      isSettled = true;
      if (loadTimeoutId !== null) {
        window.clearTimeout(loadTimeoutId);
      }
      if (printTimeoutId !== null) {
        window.clearTimeout(printTimeoutId);
      }
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", handleError);
      window.removeEventListener("focus", handleWindowFocus);
      try {
        frameWindow?.removeEventListener("afterprint", handleAfterPrint);
      } catch {
      }
      iframe.remove();
      resolve(result);
    };

    const handleError = () => {
      settle({ success: false, reason: "load_error" });
    };

    const handleAfterPrint = () => {
      settle({ success: true });
    };

    const handleWindowFocus = () => {
      if (!didStartPrint) return;
      settle({ success: true });
    };

    const handleLoad = () => {
      if (loadTimeoutId !== null) {
        window.clearTimeout(loadTimeoutId);
        loadTimeoutId = null;
      }
      frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        settle({ success: false, reason: "missing_window" });
        return;
      }
      try {
        frameWindow.addEventListener("afterprint", handleAfterPrint);
      } catch {
      }
      window.addEventListener("focus", handleWindowFocus, { once: true });
      try {
        frameWindow.focus();
        didStartPrint = true;
        frameWindow.print();
      } catch {
        settle({ success: false, reason: "print_error" });
        return;
      }
      printTimeoutId = window.setTimeout(() => {
        settle({ success: false, reason: "print_timeout" });
      }, 5000);
    };

    iframe.setAttribute("aria-hidden", "true");
    if (iframeSandbox) {
      iframe.setAttribute("sandbox", iframeSandbox);
    }
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.addEventListener("load", handleLoad, { once: true });
    iframe.addEventListener("error", handleError, { once: true });
    loadTimeoutId = window.setTimeout(() => {
      settle({ success: false, reason: "load_timeout" });
    }, 15000);
    iframe.src = url;
    document.body.appendChild(iframe);
  });
}

// ── Helpers ────────────────────────────────────────────────────────────

function normalizeDocumentType(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isMatchingDocType(normalizedType: string, docType: GeneratableDocType) {
  if (docType === "bol") {
    return (
      normalizedType === "bol" ||
      normalizedType.includes("bill_of_lading") ||
      normalizedType.includes("bill")
    );
  }

  if (docType === "order_resume") {
    return (
      normalizedType === "order_resume" ||
      normalizedType === "delivery_order" ||
      normalizedType === "deliveryorder" ||
      (normalizedType.includes("delivery") && normalizedType.includes("order"))
    );
  }

  return normalizedType === "invoice";
}

export function hasStoredDocumentForType(attachments: any[], docType: GeneratableDocType) {
  return attachments.some((attachment) => {
    const url = String(attachment?.url ?? "").trim();
    if (!url) return false;
    return isMatchingDocType(normalizeDocumentType(attachment?.type), docType);
  });
}

export function buildDocumentLinks(order: any, attachments: any[]): DocumentLink[] {
  const links: DocumentLink[] = [];
  const seenUrls = new Set<string>();

  const addLink = (link: DocumentLink) => {
    const normalizedUrl = String(link.url ?? "").trim();
    if (!normalizedUrl || seenUrls.has(normalizedUrl)) return;
    seenUrls.add(normalizedUrl);
    links.push({ ...link, url: normalizedUrl });
  };

  const shippingBlocked = order.test === true || order.status === "held_for_customer" ||
    (order.customerStorage && order.customerStorage.status !== "released");
  const liveStorageDo = Boolean(order.customerStorage || order.hasOwnedCoffee || order.customerHoldDays || order.items?.some((item: any) => item.fundedOrderId));
  const coreDocs: { type: GeneratableDocType; label: string }[] = [
    ...(!shippingBlocked ? [{ type: "bol" as const, label: DOC_TYPE_LABELS.bol }] : []),
    { type: "order_resume", label: order.status === "held_for_customer" ? "Holding DO" : DOC_TYPE_LABELS.order_resume },
  ];

  for (const coreDoc of coreDocs) {
    const storedAttachment = (liveStorageDo || shippingBlocked) && coreDoc.type === "order_resume" ? undefined : attachments.find((attachment) =>
      isMatchingDocType(normalizeDocumentType(attachment?.type), coreDoc.type),
    );
    const liveUrl = getDocumentUrl(coreDoc.type, order);
    addLink({
      key: `core-${coreDoc.type}`,
      label: coreDoc.label,
      url: storedAttachment?.url ?? liveUrl,
      documentType: storedAttachment?.type ?? coreDoc.type,
      source: storedAttachment?.url ? "stored" : "live",
      documentId: storedAttachment?.url
        ? (storedAttachment?.id != null ? String(storedAttachment.id) : undefined)
        : undefined,
    });
  }

  const userInvoiceUrl =
    typeof order?.invoiceUrl === "string" && order.invoiceUrl.trim().length > 0
      ? order.invoiceUrl.trim()
      : typeof order?.invoicePath === "string" && order.invoicePath.trim().length > 0
        ? order.invoicePath.trim()
        : null;

  if (userInvoiceUrl) {
    addLink({
      key: "invoice-user",
      label: "Invoice (User)",
      url: userInvoiceUrl,
      documentType: "invoice",
      source: "external",
    });
  }

  for (const attachment of attachments) {
    const attachmentUrl = String(attachment?.url ?? "").trim();
    if (!attachmentUrl) continue;

    const normalizedType = normalizeDocumentType(attachment?.type);
    if (normalizedType === "invoice") continue;
    if ((liveStorageDo || shippingBlocked) && isMatchingDocType(normalizedType, "order_resume")) continue;
    if (shippingBlocked && (isMatchingDocType(normalizedType, "bol") || normalizedType.includes("label"))) continue;
    const knownLabel = normalizedType in DOC_TYPE_LABELS
      ? DOC_TYPE_LABELS[normalizedType as GeneratableDocType]
      : normalizedType === "shipping_label"
        ? "Shipping Label"
      : undefined;

    addLink({
      key: `attachment-${attachment?.id ?? attachmentUrl}`,
      label:
        attachment?.label ??
        knownLabel ??
        attachment?.metadata?.fileName ??
        attachment?.type ??
        "Document",
      url: attachmentUrl,
      documentType: String(attachment?.type ?? "document"),
      source: "stored",
      documentId: attachment?.id != null ? String(attachment.id) : undefined,
    });
  }

  return links;
}

// ── Component ──────────────────────────────────────────────────────────

export function OrderDocumentsCell({
  row,
  canUploadShippingLabels = false,
  canDeleteDocuments,
  onRequestManualShippingLabelUpload,
}: {
  row: any;
  canUploadShippingLabels?: boolean;
  canDeleteDocuments?: boolean;
  onRequestManualShippingLabelUpload?: (order: any) => void;
}) {
  useAppLocale();
  const { authUserId, userRole } = useAuthIdentity();
  const deleteOrderDocument = useAction(api.logistics.deleteOrderDocument);
  const canDeleteStoredDocuments =
    (canDeleteDocuments ?? hasPermission(userRole, "logistics:edit")) &&
    Boolean(authUserId);
  const orderId = row.original._id ?? row.original.id;
  const orderNumber = row.original.orderNumber ?? orderId;
  const attachments = (row.original.attachments as any[] | undefined) ?? [];
  const documentLinks = React.useMemo(
    () => buildDocumentLinks(row.original, attachments),
    [attachments, row.original],
  );
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [activeDocKey, setActiveDocKey] = React.useState<string | null>(null);
  const [isActiveDocReady, setIsActiveDocReady] = React.useState(false);
  const [isPrintingAll, setIsPrintingAll] = React.useState(false);
  const [printingDocIndex, setPrintingDocIndex] = React.useState<number | null>(null);
  const [printAllError, setPrintAllError] = React.useState<string | null>(null);
  const [documentPendingDeletion, setDocumentPendingDeletion] =
    React.useState<DocumentLink | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = React.useState(false);
  const previewFrameRef = React.useRef<HTMLIFrameElement | null>(null);
  const activeDoc =
    documentLinks.find((doc) => doc.key === activeDocKey) ?? documentLinks[0] ?? null;
  const firstDoc = documentLinks[0] ?? null;
  const showManualShippingLabelUpload =
    canUploadShippingLabels && row.original.test !== true && row.original.status !== "held_for_customer" &&
    (!row.original.customerStorage || row.original.customerStorage.status === "released") &&
    typeof onRequestManualShippingLabelUpload === "function";
  const canDeleteActiveDoc =
    canDeleteStoredDocuments &&
    activeDoc?.source === "stored" &&
    Boolean(activeDoc?.documentId);
  const baseDocumentUrls = React.useMemo(
    () => new Map(documentLinks.map((doc) => [doc.key, resolveDocumentUrl(doc.url)])),
    [documentLinks],
  );
  const documentUrls = baseDocumentUrls;

  React.useEffect(() => {
    return () => {
      if (typeof URL.revokeObjectURL !== "function") return;

      for (const resolvedUrl of documentUrls.values()) {
        const { objectUrl } = resolvedUrl;
        if (isBlobObjectUrl(objectUrl)) {
          URL.revokeObjectURL(objectUrl);
        }
      }
    };
  }, [documentUrls]);
  const printableDocumentLinks = React.useMemo(
    () =>
      documentLinks
        .map((doc) => {
          const resolvedUrl = documentUrls.get(doc.key) ?? resolveDocumentUrl(doc.url);
          return {
            doc,
            resolvedUrl,
            iframeSandbox: getIframeSandboxForDocumentUrl(resolvedUrl.iframeUrl, doc.url),
          };
        })
        .filter((entry) => !isDirectFileDocument(entry.doc) && Boolean(entry.resolvedUrl.iframeUrl)),
    [documentLinks, documentUrls],
  );
  const activeDocUrls = activeDoc
    ? documentUrls.get(activeDoc.key) ?? resolveDocumentUrl(activeDoc.url)
    : { iframeUrl: null, openInNewTabUrl: null, objectUrl: null };
  const activeDocUsesIframe = activeDoc ? !isDirectFileDocument(activeDoc) : false;
  const activeDocPreviewUrl = activeDocUsesIframe
    ? activeDocUrls.iframeUrl
    : activeDocUrls.objectUrl ?? activeDocUrls.iframeUrl ?? activeDocUrls.openInNewTabUrl;
  const activeDocDisplayUrl = activeDocPreviewUrl ?? activeDocUrls.openInNewTabUrl;
  const activeDocDisplayFileKind = activeDocDisplayUrl ? getDocumentFileKind(activeDocDisplayUrl) : "other";
  const activeDocOriginalFileKind = activeDoc?.url ? getDocumentFileKind(activeDoc.url) : "other";
  const activeDocFileKind = activeDocDisplayFileKind === "other" ? activeDocOriginalFileKind : activeDocDisplayFileKind;
  const activeDocIframeSandbox = getIframeSandboxForDocumentUrl(
    activeDocPreviewUrl,
    activeDoc?.url,
  );

  React.useEffect(() => {
    if (!activeDocKey && firstDoc) {
      setActiveDocKey(firstDoc.key);
    }
  }, [activeDocKey, firstDoc]);

  React.useEffect(() => {
    void activeDoc?.key;
    void activeDocPreviewUrl;
    setIsActiveDocReady(false);
  }, [activeDoc?.key, activeDocPreviewUrl]);

  React.useEffect(() => {
    if (!activeDoc || activeDocUsesIframe || !activeDocPreviewUrl) return;
    setIsActiveDocReady(true);
  }, [activeDoc, activeDocPreviewUrl, activeDocUsesIframe]);

  const handlePrintActiveDocument = React.useCallback(() => {
    if (!activeDocUsesIframe || !activeDocUrls.iframeUrl) return;
    const frameWindow = previewFrameRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  }, [activeDocUrls.iframeUrl, activeDocUsesIframe]);

  const handlePrintAllDocuments = React.useCallback(async () => {
    if (isPrintingAll || printableDocumentLinks.length === 0) return;
    setPrintAllError(null);
    setIsPrintingAll(true);
    try {
      for (const [index, entry] of printableDocumentLinks.entries()) {
        setPrintingDocIndex(index);
        const result = await printDocumentByUrl(
          entry.resolvedUrl.iframeUrl!,
          entry.iframeSandbox,
        );
        if (!result.success) {
          setPrintAllError(m.orders_print_all_failed({ label: entry.doc.label }));
          break;
        }
      }
    } finally {
      setPrintingDocIndex(null);
      setIsPrintingAll(false);
    }
  }, [isPrintingAll, printableDocumentLinks]);

  const handleRequestManualShippingLabelUpload = React.useCallback(() => {
    if (!showManualShippingLabelUpload) return;
    setIsDialogOpen(false);
    onRequestManualShippingLabelUpload?.(row.original);
  }, [onRequestManualShippingLabelUpload, row.original, showManualShippingLabelUpload]);

  const handleConfirmDeleteDocument = React.useCallback(async () => {
    const target = documentPendingDeletion;
    if (!target?.documentId || !authUserId || !orderId) return;
    setIsDeletingDocument(true);
    try {
      await deleteOrderDocument({
        authUserId,
        orderId: orderId as any,
        documentId: target.documentId as any,
      });
      toast.success(
        m.orders_delete_document_success({
          label: target.label,
          orderNumber: String(orderNumber),
        }),
      );
      setActiveDocKey(null);
      setDocumentPendingDeletion(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : m.orders_delete_document_error(),
      );
    } finally {
      setIsDeletingDocument(false);
    }
  }, [authUserId, deleteOrderDocument, documentPendingDeletion, orderId, orderNumber]);

  if (documentLinks.length === 0 && !isDialogOpen) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="xs"
        variant="outline"
        className="h-7 gap-1.5"
        onClick={() => setIsDialogOpen(true)}
      >
        <FileText className="size-3.5" />
        {m.orders_docs_count({ count: documentLinks.length })}
      </Button>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="h-[86vh] max-w-[96vw] overflow-hidden p-0 sm:max-w-[96vw]">
          <div className="flex h-full min-h-0 flex-col">
            <DialogHeader className="border-b px-5 py-4">
              <DialogTitle>{m.orders_documents_title({ orderNumber: String(orderNumber) })}</DialogTitle>
              <DialogDescription>
                {m.orders_documents_description()}
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:flex-row lg:items-start">
              <div className="flex w-full shrink-0 flex-col gap-3 lg:w-72">
                <div className="flex flex-col gap-2">
                  {documentLinks.map((doc) => {
                    const isActive = activeDoc?.key === doc.key;
                    return (
                      <Button
                        key={doc.key}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        className="h-10 w-full justify-between rounded-lg px-3"
                        onClick={() => setActiveDocKey(doc.key)}
                      >
                        <span className="truncate">{doc.label}</span>
                        <span className="text-[10px] uppercase opacity-80">
                          {getDocumentSourceLabel(doc.source)}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                {showManualShippingLabelUpload ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-10 w-full justify-start gap-2 rounded-lg px-3"
                    onClick={handleRequestManualShippingLabelUpload}
                  >
                    <Upload className="size-4" />
                    {m.orders_upload_label()}
                  </Button>
                ) : null}
                {activeDoc ? (
                  <div className="flex flex-col gap-2 rounded-xl border bg-muted/20 p-2">
                    <Button
                      size="sm"
                      className="h-10 w-full gap-1.5 rounded-lg"
                      disabled={!activeDocUsesIframe || !activeDocUrls.iframeUrl || !isActiveDocReady || isPrintingAll}
                      onClick={handlePrintActiveDocument}
                    >
                      <Printer className="size-4" />
                      {m.orders_print_document()}
                    </Button>
                    {printableDocumentLinks.length > 1 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 w-full gap-1.5 rounded-lg"
                        disabled={isPrintingAll}
                        onClick={() => void handlePrintAllDocuments()}
                      >
                        {isPrintingAll ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            {m.orders_printing_documents_progress({
                              current: String((printingDocIndex ?? 0) + 1),
                              total: String(printableDocumentLinks.length),
                            })}
                          </>
                        ) : (
                          <>
                            <Printer className="size-4" />
                            {m.orders_print_all_documents()}
                          </>
                        )}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-10 w-full rounded-lg"
                      disabled={!activeDocUrls.openInNewTabUrl}
                      onClick={() => {
                        if (!activeDocUrls.openInNewTabUrl) return;
                        window.open(activeDocUrls.openInNewTabUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {m.orders_open_in_new_tab()}
                    </Button>
                    {canDeleteActiveDoc ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 w-full gap-1.5 rounded-lg border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={isDeletingDocument}
                        onClick={() => setDocumentPendingDeletion(activeDoc)}
                      >
                        <Trash2 className="size-4" />
                        {m.orders_delete_document()}
                      </Button>
                    ) : null}
                    {printAllError ? (
                      <p className="px-1 text-xs text-destructive">{printAllError}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-muted/20 p-3 lg:p-4">
                {activeDoc && (activeDocPreviewUrl || activeDocUrls.openInNewTabUrl) ? (
                  <div className="mx-auto h-full min-h-full w-full max-w-[1200px] overflow-hidden rounded-lg border bg-white shadow-sm">
                    {activeDocUsesIframe && activeDocPreviewUrl ? (
                      <iframe
                        key={activeDoc.key}
                        ref={previewFrameRef}
                        src={activeDocPreviewUrl}
                        title={activeDoc.label}
                        className="h-full min-h-[68vh] w-full border-0 bg-white align-top"
                        loading="lazy"
                        sandbox={activeDocIframeSandbox}
                        onLoad={() => setIsActiveDocReady(true)}
                      />
                    ) : activeDocFileKind === "image" ? (
                      <div className="flex h-full min-h-[68vh] items-center justify-center bg-white p-4">
                        <img
                          src={activeDocPreviewUrl ?? activeDocUrls.openInNewTabUrl!}
                          alt={activeDoc.label}
                          className="max-h-full w-auto max-w-full object-contain"
                        />
                      </div>
                    ) : (
                      <object
                        key={activeDoc.key}
                        data={activeDocPreviewUrl ?? activeDocUrls.openInNewTabUrl!}
                        title={activeDoc.label}
                        aria-label={activeDoc.label}
                        type={activeDocFileKind === "text" ? "text/plain" : "application/pdf"}
                        className="h-full min-h-[68vh] w-full"
                      >
                        <div className="flex h-full min-h-[68vh] items-center justify-center p-6 text-sm text-muted-foreground">
                          {m.orders_document_preview_unavailable()}
                        </div>
                      </object>
                    )}
                  </div>
                ) : activeDoc ? (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                    {m.orders_document_preview_unavailable()}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                    {m.orders_no_documents_available()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={Boolean(documentPendingDeletion)}
        onOpenChange={(open) => {
          if (!open && !isDeletingDocument) setDocumentPendingDeletion(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {m.orders_delete_document_confirm_title()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {documentPendingDeletion
                ? m.orders_delete_document_confirm_description({
                    label: documentPendingDeletion.label,
                    orderNumber: String(orderNumber),
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingDocument}>
              {m.cancel()}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingDocument}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmDeleteDocument();
              }}
            >
              {isDeletingDocument ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {m.orders_delete_document_deleting()}
                </>
              ) : (
                m.orders_delete_document_action()
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
